/**
 * AI 학습함 API — 통화 후 미응대 감지 결과 inbox. V1257 (작업 #4)
 *
 * 백엔드 `LearningInboxController` (`/api/learning-inbox`):
 *   GET  /                  리스트 (KB_ADDITION/KB_DIAGNOSIS 한정)
 *   GET  /unread-count      사이드바 배지
 *   GET  /{id}              상세
 *   PUT  /{id}/answer       답변/카테고리 (ORG_POWER+)
 *   POST /{id}/approve      승인 → KB 반영 (ORG_ADMIN)
 *   POST /{id}/reject       거부 (ORG_ADMIN)
 *
 * 타입은 LearningInboxController 의 toSummaryDto/toDetailDto 와 1:1 대응.
 * backend 가 정답 — 가정 금지 (웨이브 1 #2 API 계약 불일치 전례).
 */

import apiClient from "./client";

/** 제안 종류 — AI 학습함은 이 2종만 (PROMPT/RULE/CLASSIFICATION 은 제외) */
export type LearningInboxType = "KB_ADDITION" | "KB_DIAGNOSIS";

/**
 * 제안 상태 4-state (V1257 확장).
 * PENDING → IN_PROGRESS(답변 작성중) → PENDING_APPROVAL(승인 대기) → APPROVED/REJECTED
 */
export type LearningInboxStatus =
  | "PENDING"
  | "IN_PROGRESS"
  | "PENDING_APPROVAL"
  | "APPROVED"
  | "REJECTED";

/** 리스트 요약 (toSummaryDto) */
export interface LearningInboxSummary {
  id: number;
  suggestionType: LearningInboxType;
  title: string;
  status: LearningInboxStatus;
  priority: number;
  /** 같은 질문 재발 횟수 (evidence_data.occurrence_count, 기본 1) */
  occurrenceCount: number;
  createdAt: string;
}

/** Type 2 RAG 청크 — backend evidence_data.retrieved_chunks 원소 (score 등은 UI 미표시) */
export interface RetrievedChunk {
  text?: string;
  content?: string;
  source?: string;
  [key: string]: unknown;
}

/**
 * evidence_data JSONB 구조.
 * snake_case — backend 가 Map 을 그대로 직렬화하므로 키는 원본 유지.
 */
export interface LearningInboxEvidence {
  question?: string;
  occurrence_count?: number;
  related_consult_ids?: number[];
  transcript_excerpt?: string;
  /** Type 2 — AI가 통화 시점에 찾은 자료 */
  retrieved_chunks?: RetrievedChunk[];
  source_kb_items?: { id: number; title: string }[];
  /** 담당자가 직접 수정한 질문 제목 (4-3). 미설정 시 question 사용 */
  custom_title?: string;
  [key: string]: unknown;
}

/** suggestedValue JSONB — 담당자 답변 작성 결과 */
export interface LearningInboxSuggestedValue {
  answer?: string;
  /** 다중 카테고리 태그 (4-2 — 정식 계약) */
  categories?: string[];
  [key: string]: unknown;
}

/** 상세 (toDetailDto = summary + 아래 필드) */
export interface LearningInboxDetail extends LearningInboxSummary {
  description: string | null;
  evidenceData: LearningInboxEvidence | null;
  suggestedValue: LearningInboxSuggestedValue | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
}

/** 답변 저장 요청 (PUT /{id}/answer) */
export interface LearningInboxAnswerRequest {
  answer: string;
  /** 다중 카테고리 태그 (4-2 — 정식 계약, 단수 category 아님) */
  categories: string[];
  /** 담당자가 수정한 질문 제목 (4-3 — 선택, 미전송 시 자동 제목 유지) */
  title?: string;
  /** true 면 PENDING_APPROVAL(승인 대기), false 면 IN_PROGRESS(작성중) */
  submitForApproval: boolean;
}

/** 승인 응답 (POST /{id}/approve) */
export interface LearningInboxApproveResponse {
  suggestionId: number;
  knowledgeBaseItemId: number;
  status: LearningInboxStatus;
}

/** 거부 응답 (POST /{id}/reject) */
export interface LearningInboxRejectResponse {
  suggestionId: number;
  status: LearningInboxStatus;
}

export const learningInboxApi = {
  /** AI 학습함 리스트 (KB_ADDITION/KB_DIAGNOSIS 한정 — backend 필터) */
  list: async (): Promise<LearningInboxSummary[]> => {
    const response = await apiClient.get<LearningInboxSummary[]>(
      "/api/learning-inbox",
    );
    return response.data;
  },

  /** 미처리 제안 수 — 사이드바 unread 배지 */
  unreadCount: async (): Promise<number> => {
    const response = await apiClient.get<{ count: number }>(
      "/api/learning-inbox/unread-count",
    );
    return response.data.count;
  },

  /** 제안 상세 */
  detail: async (id: number): Promise<LearningInboxDetail> => {
    const response = await apiClient.get<LearningInboxDetail>(
      `/api/learning-inbox/${id}`,
    );
    return response.data;
  },

  /** 답변/카테고리 저장 (ORG_POWER+) — 상세 DTO 반환 */
  answer: async (
    id: number,
    data: LearningInboxAnswerRequest,
  ): Promise<LearningInboxDetail> => {
    const response = await apiClient.put<LearningInboxDetail>(
      `/api/learning-inbox/${id}/answer`,
      data,
    );
    return response.data;
  },

  /** 승인 → KB 반영 (ORG_ADMIN) */
  approve: async (id: number): Promise<LearningInboxApproveResponse> => {
    const response = await apiClient.post<LearningInboxApproveResponse>(
      `/api/learning-inbox/${id}/approve`,
    );
    return response.data;
  },

  /** 거부 (ORG_ADMIN) */
  reject: async (
    id: number,
    reason: string,
  ): Promise<LearningInboxRejectResponse> => {
    const response = await apiClient.post<LearningInboxRejectResponse>(
      `/api/learning-inbox/${id}/reject`,
      { reason },
    );
    return response.data;
  },
};
